import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, type RequestContext } from "~/shared/api/client";
import type { AttachmentPolicyDto, CreateArtifactGenerationResponseDto } from "~/shared/types/chats";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toCtx(input?: {
    projectId?: string;
    sceneId?: string;
    workspaceId?: string;
}): RequestContext | undefined {
    if (!input?.projectId && !input?.sceneId && !input?.workspaceId) return undefined;
    return { projectId: input?.projectId, sceneId: input?.sceneId, workspaceId: input?.workspaceId };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const chatKeys = {
    all: ["chats"] as const,
    attachmentPolicy: ["chats", "attachmentPolicy"] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useAttachmentPolicy(ctx?: { projectId?: string; sceneId?: string; workspaceId?: string }) {
    return useQuery({
        queryKey: chatKeys.attachmentPolicy,
        queryFn: () => apiGet<AttachmentPolicyDto>("/v2/files/settings", undefined, toCtx(ctx)),
    });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCancelGenerationJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: {
            generationJobId: string;
            projectId?: string;
            sceneId?: string;
            workspaceId?: string;
        }) => apiPost(`/v2/artifacts/generation/${input.generationJobId}/cancel`, undefined, toCtx(input)),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: chatKeys.all });
        },
    });
}

export function useRetryGenerationJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: {
            generationJobId: string;
            projectId?: string;
            sceneId?: string;
            workspaceId?: string;
        }) =>
            apiPost<CreateArtifactGenerationResponseDto>(
                `/v2/artifacts/generation/${input.generationJobId}/retry`,
                undefined,
                toCtx(input),
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: chatKeys.all });
        },
    });
}
